import React, { useState, forwardRef, useContext } from 'react';
import { Body } from 'js/design_system/Body';
import Badge from 'js/design_system/Badge';
import { Icon } from 'js/design_system/Icon';
import styled, { css } from 'astroturf/react';
import { trackClick } from 'js/utils/trackClick';
import { WorkData } from '../types';
import { AddToListButton } from './AddToListButton';
import { Button } from 'js/design_system/Button';
import { PaperEvaluationChatButton } from './PaperEvaluationChatButton';
import { CiterightContext } from '../CiterightContextProvider';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';

const workCardSeparator = css`
  &:not(:first-child) {
    border-top: 1px solid var(--stroke-light);
  }
`;

const WorkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 8px;
  background: var(--background-white, #ffffff);
  transition: background-color 0.3s ease;
`;

const HighlightedWorkRow = styled(WorkRow)`
  animation: highlightPulse 2s ease-out;

  @keyframes highlightPulse {
    0% {
      background-color: #fef3cd;
    }
    50% {
      background-color: #fef3cd;
    }
    100% {
      background-color: var(--background-white, #ffffff);
    }
  }
`;

const AbstractQuote = styled.div`
  background-color: var(--brand-colors-neutral-200);
  border-left: 4px solid var(--brand-colors-blue-400);
  border-radius: 4px;
  padding: 8px 12px;
  margin: 4px 0;
  cursor: pointer;
  text-align: left;
`;

const ShowMoreLink = styled.button`
  background: none;
  border: none;
  color: var(--brand-colors-blue-600);
  cursor: pointer;
  font-size: inherit;
  text-decoration: underline;
  padding: 0;
  margin-left: 4px;

  &:hover {
    color: var(--brand-colors-blue-700);
  }
`;

const AbstractLink = styled.a`
  color: var(--brand-colors-blue-600);
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: var(--brand-colors-blue-700);
  }
`;

const ReferenceIdBadge = styled.span`
  display: inline-block;
  color: var(--brand-colors-blue-700);
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
  margin-bottom: 4px;
  margin-right: 8px;
`;

const ImprovedCitationNote = styled.div`
  background-color: var(--brand-colors-green-100, #e8f5e9);
  border-left: 4px solid var(--brand-colors-green);
  border-radius: 4px;
  padding: 8px 12px;
  margin: 8px 0;
  text-align: left;
`;

interface WorkCardProps {
  workData: WorkData;
  isHighlighted?: boolean;
  onClick?: () => void;
  showAddToListButton?: boolean;
  showPaperEvaluationChatButton?: boolean;
  claimId?: string;
  claimText?: string;
  hasVote?: boolean;
  isFiltered?: boolean;
  claimWorks?: WorkData[];
  claimReferences?: WorkData[];
  pageContext?: 'mini_report' | 'inline_report' | 'preprint_report';
  citedTitles?: Set<string>;
}

const WorkCard = forwardRef<HTMLDivElement, WorkCardProps>(
  (
    {
      workData,
      isHighlighted = false,
      onClick,
      showAddToListButton = true,
      showPaperEvaluationChatButton = true,
      claimId,
      claimText,
      hasVote = false,
      isFiltered = false,
      pageContext,
      citedTitles,
    },
    ref,
  ) => {
    const {
      work,
      impact_factor,
      reference_identifier,
      relevance_score,
      reasoning,
    } = workData;
    const context = useContext(CiterightContext);
    const [showAbstract, setShowAbstract] = useState(false);
    const [showAllAuthors, setShowAllAuthors] = useState(false);
    const [isCopied, setIsCopied] = useState(false);

    // Find first matching reference to determine if already cited and get reference ID
    // Check context first, then fall back to citedTitles Set for mini reports
    const matchingRef = context?.report?.references?.find(
      (reference) =>
        reference.work?.title?.toLowerCase() === work?.title?.toLowerCase(),
    );

    // For mini reports without context, check if title is in citedTitles Set
    const isCitedInMiniReport =
      !matchingRef &&
      citedTitles &&
      work?.title &&
      citedTitles.has(work.title.toLowerCase().trim());

    const already_cited = !!matchingRef || isCitedInMiniReport;
    const displayReferenceId =
      reference_identifier || matchingRef?.reference_identifier || null;

    const claimReferences = context?.report?.claims
      .find((claim) => claim.id === claimId)
      ?.reference_ids.map((id) =>
        context?.report?.references.find(
          (reference) => reference.reference_identifier === id,
        ),
      )
      .filter((reference) => reference !== undefined);

    // Check if this work is a better fit for the claim than current references
    // Is this work newer than ALL works in claimReferences?
    const isNewerThanClaimReferences =
      claimReferences &&
      claimReferences.length > 0 &&
      work.publicationYear &&
      claimReferences.every(
        (reference) =>
          reference.work.publicationYear &&
          work.publicationYear > reference.work.publicationYear,
      );

    // Does this work have a higher impact factor than ALL works in claimReferences?
    const hasHigherImpactFactorThanClaimReferences =
      claimReferences &&
      claimReferences.length > 0 &&
      impact_factor?.impact_factor &&
      claimReferences.every(
        (reference) =>
          reference.impact_factor?.impact_factor &&
          impact_factor.impact_factor > reference.impact_factor.impact_factor,
      );

    // Is this work already cited elsewhere but not in claimReferences?
    const alreadyCitedButNotInClaimReferences =
      already_cited &&
      claimReferences &&
      claimReferences.length > 0 &&
      !claimReferences.some(
        (reference) =>
          reference.work?.title?.toLowerCase() === work?.title?.toLowerCase(),
      );

    // Normalize DOI by removing URL prefix if present
    const normalizeDoiForDisplay = (doi: string): string => {
      if (!doi) return '';
      // Remove https://doi.org/ or http://doi.org/ prefix and trim whitespace
      return doi.replace(/^https?:\/\/doi\.org\//i, '').trim();
    };

    const displayDoi = normalizeDoiForDisplay(work.doi);

    const handleCopyDoi = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!work.doi) return;

      const copyToClipboard = (text: string) => {
        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          return navigator.clipboard.writeText(text);
        }

        // Fallback for older browsers or insecure contexts
        return new Promise<void>((resolve, reject) => {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();

          try {
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            if (successful) {
              resolve();
            } else {
              reject(new Error('execCommand failed'));
            }
          } catch (err) {
            document.body.removeChild(textArea);
            reject(err);
          }
        });
      };

      copyToClipboard(displayDoi)
        .then(() => {
          setIsCopied(true);
          trackClick({
            source: 'citeright.reference_doi_copy',
            entityId: context?.reportId ?? undefined,
            extra: {
              page_context: pageContext,
              suggestion_source: workData.source,
            },
          });
          setTimeout(() => setIsCopied(false), 2000);
        })
        .catch((err) => {
          console.error('Failed to copy DOI:', err);
        });
    };

    const abstractPrefix = work.abstract?.toLowerCase().startsWith('abstract')
      ? ''
      : 'Abstract: ';

    // Abstract truncation length
    const truncateLength = 200;

    // Author truncation logic
    const shouldTruncateAuthors = work.authors.length > 7;

    // Determine the improved citation message
    const getImprovedCitationMessage = () => {
      if (isNewerThanClaimReferences) {
        return 'This citation is newer than your current citation for the claim';
      }
      if (hasHigherImpactFactorThanClaimReferences) {
        return 'This citation has a higher impact factor than your current citation for the claim';
      }
      if (alreadyCitedButNotInClaimReferences) {
        return 'You already cite this work elsewhere and it looks like a good fit for this claim as well';
      }
      return null;
    };

    const improvedCitationMessage = getImprovedCitationMessage();

    const RowComponent = isHighlighted ? HighlightedWorkRow : WorkRow;

    return (
      <ImpressionWatcher
        className={workCardSeparator}
        source="citeright.reference_impression"
        entity_id={work.id}
        entity_type={ImpressionEntity.OpenalexWork}
        active={!!work.id}
        extra={{
          suggestion_source: workData.source,
          page_context: pageContext,
          report_id: context?.reportId,
        }}
      >
        <div
          ref={ref}
          style={{
            opacity: isFiltered ? 0.6 : 1,
            position: 'relative',
          }}
        >
          <RowComponent
            onClick={onClick}
            style={{
              cursor: onClick ? 'pointer' : 'default',
              background: hasVote
                ? 'transparent'
                : isFiltered
                  ? 'var(--brand-colors-neutral-100, #fafafa)'
                  : undefined,
              transition: hasVote ? 'none' : undefined,
            }}
          >
            <div style={{ flex: 1, minWidth: 0, padding: '8px 0' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 8,
                }}
              >
                <Body
                  size="md"
                  bold
                  style={{ flex: 1, minWidth: 0, textAlign: 'left', gap: 8 }}
                >
                  {displayReferenceId && (
                    <ReferenceIdBadge>[{displayReferenceId}]</ReferenceIdBadge>
                  )}
                  {work.doi ? (
                    <a
                      href={`https://doi.org/${displayDoi}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      // Tracks clicking the title link to visit the DOI URL (distinct from copying DOI)
                      onClick={() => {
                        trackClick({
                          source: 'citeright.reference_title_link',
                          entityId: context?.reportId ?? undefined,
                          extra: {
                            page_context: pageContext,
                            suggestion_source: workData.source,
                          },
                        });
                      }}
                      style={{
                        color: 'var(--brand-colors-blue-600)',
                        textDecoration: 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      {work.title}
                      &nbsp;
                      <span
                        style={{
                          userSelect: 'none',
                          display: 'inline-block',
                          verticalAlign: 'middle',
                          marginLeft: '4px',
                        }}
                      >
                        <Icon name="open_in_new" size={16} />
                      </span>
                    </a>
                  ) : (
                    work.title
                  )}
                </Body>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    flexDirection: 'row',
                  }}
                >
                  {showAddToListButton && (
                    <AddToListButton
                      reference={workData}
                      size="sm"
                      claimId={claimId}
                      claimText={claimText}
                      pageContext={pageContext}
                    />
                  )}
                  {showPaperEvaluationChatButton && (
                    <PaperEvaluationChatButton work={workData} />
                  )}
                </div>
              </div>
              <Body size="sm">
                {shouldTruncateAuthors && !showAllAuthors ? (
                  <>
                    {work.authors
                      .slice(0, 3)
                      .map((a) => a.name)
                      .join(', ')}
                    ,{' '}
                    <ShowMoreLink
                      onClick={(event) => {
                        event.stopPropagation();
                        setShowAllAuthors(true);
                      }}
                    >
                      +{work.authors.length - 4} more
                    </ShowMoreLink>
                    , {work.authors[work.authors.length - 1].name}
                  </>
                ) : (
                  <>
                    {work.authors.map((a) => a.name).join(', ')}
                    {shouldTruncateAuthors && (
                      <ShowMoreLink
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowAllAuthors(false);
                        }}
                      >
                        {' '}
                        Show less
                      </ShowMoreLink>
                    )}
                  </>
                )}
              </Body>
              <Body size="sm">
                {work.publication}
                {work.openAccess && ' • Open access'}
              </Body>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  marginTop: 4,
                  flexWrap: 'wrap',
                }}
              >
                {workData.source === 'citation_search_agent' && (
                  <>
                    {!reasoning && (
                      <Badge
                        text="Evaluating..."
                        icon="hourglass"
                        iconSize={12}
                        backgroundColor="var(--brand-colors-blue-500)"
                        style={{
                          fontSize: '11px',
                          padding: '2px 6px',
                          height: '20px',
                        }}
                      />
                    )}
                    {workData.validated === true && (
                      <Badge
                        text="Validated"
                        icon="verified"
                        iconSize={12}
                        backgroundColor="var(--brand-colors-green)"
                        style={{
                          fontSize: '11px',
                          padding: '2px 6px',
                          height: '20px',
                        }}
                      />
                    )}
                    {workData.validated === false && (
                      <Badge
                        text="Validating"
                        icon="hourglass"
                        iconSize={12}
                        style={{
                          fontSize: '11px',
                          padding: '2px 6px',
                          height: '20px',
                        }}
                      />
                    )}
                  </>
                )}
              </div>
              {improvedCitationMessage && (
                <ImprovedCitationNote>
                  <Body size="sm" style={{ fontWeight: 500 }}>
                    {improvedCitationMessage}
                  </Body>
                </ImprovedCitationNote>
              )}
              {reasoning && (
                <Body size="sm" style={{ marginTop: 8, textAlign: 'left' }}>
                  {reasoning}
                  {work.abstract && !showAbstract && (
                    <>
                      {' '}
                      <AbstractLink
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          trackClick({
                            source: 'citeright.reference_abstract_show_more',
                            entityId: context?.reportId ?? undefined,
                            extra: {
                              page_context: pageContext,
                              suggestion_source: workData.source,
                            },
                          });
                          setShowAbstract(true);
                        }}
                      >
                        Show&nbsp;abstract&nbsp;
                        <Icon
                          name="chevron_right"
                          size={16}
                          style={{ verticalAlign: 'middle' }}
                        />
                      </AbstractLink>
                    </>
                  )}
                </Body>
              )}
              {work.abstract && (!reasoning || showAbstract) && (
                <AbstractQuote style={{ marginTop: 8 }}>
                  <Body size="sm">
                    {abstractPrefix}
                    {/* When reasoning is null and not expanded, show truncated if > 200 chars */}
                    {!reasoning &&
                    !showAbstract &&
                    work.abstract.length > truncateLength
                      ? work.abstract.slice(0, truncateLength).trim() + '...'
                      : work.abstract}
                    {/* Show expand link when: no reasoning and abstract > 200 and not expanded */}
                    {!reasoning &&
                      !showAbstract &&
                      work.abstract.length > truncateLength && (
                        <>
                          {' '}
                          <AbstractLink
                            href="#"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              trackClick({
                                source:
                                  'citeright.reference_abstract_show_more',
                                entityId: context?.reportId ?? undefined,
                                extra: {
                                  page_context: pageContext,
                                },
                              });
                              setShowAbstract(true);
                            }}
                          >
                            Show abstract{' '}
                            <Icon
                              name="chevron_right"
                              size={16}
                              style={{ verticalAlign: 'middle' }}
                            />
                          </AbstractLink>
                        </>
                      )}
                    {/* Show collapse link when expanded (for both reasoning and no-reasoning cases) */}
                    {showAbstract && (
                      <>
                        <br />
                        <AbstractLink
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setShowAbstract(false);
                          }}
                          style={{
                            display: 'inline-block',
                            marginTop: '4px',
                          }}
                        >
                          Show less
                        </AbstractLink>
                      </>
                    )}
                  </Body>
                </AbstractQuote>
              )}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 8,
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 4,
                    flexWrap: 'wrap',
                    flex: '1 1 auto',
                    minWidth: 'fit-content',
                  }}
                >
                  {isFiltered && (
                    <Badge
                      text="Filtered"
                      backgroundColor="var(--brand-colors-neutral-300, #cccccc)"
                      color="var(--text-primary, #141413)"
                      height="24px"
                    />
                  )}
                  {already_cited && (
                    <Badge
                      text="Already cited"
                      backgroundColor="var(--brand-colors-green-300, #8bc34a)"
                      color="var(--text-primary, #141413)"
                      height="24px"
                    />
                  )}
                  {relevance_score != null && (
                    <Badge
                      text={`Relevance: ${Math.round(relevance_score * 100)}%`}
                      backgroundColor="var(--brand-colors-blue-300, #93bbfe)"
                      color="var(--text-primary, #141413)"
                      height="24px"
                    />
                  )}
                  {work.publicationYear && (
                    <Badge
                      text={work.publicationYear.toString()}
                      backgroundColor="var(--brand-colors-blue-300, #93bbfe)"
                      color="var(--text-primary, #141413)"
                      height="24px"
                    />
                  )}
                  {work.citationCount !== undefined &&
                    work.citationCount > 0 && (
                      <Badge
                        text={`${work.citationCount} citations`}
                        backgroundColor="var(--brand-colors-blue-300, #93bbfe)"
                        color="var(--text-primary, #141413)"
                        height="24px"
                      />
                    )}
                  {(impact_factor?.impact_factor ||
                    impact_factor?.quartile) && (
                    <Badge
                      text={
                        impact_factor.impact_factor && impact_factor.quartile
                          ? `IF: ${impact_factor.impact_factor.toFixed(1)} (${
                              impact_factor.quartile
                            })`
                          : impact_factor.impact_factor
                            ? `IF: ${impact_factor.impact_factor.toFixed(1)}`
                            : impact_factor.quartile
                      }
                      backgroundColor="var(--brand-colors-blue-300, #93bbfe)"
                      color="var(--text-primary, #141413)"
                      height="24px"
                    />
                  )}
                </div>
                {work.doi && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      flex: '0 0 auto',
                      minWidth: 'fit-content',
                    }}
                  >
                    <Body
                      size="sm"
                      style={{
                        lineHeight: '16px',
                      }}
                    >
                      <span style={{ userSelect: 'none' }}>DOI: </span>
                      <a
                        href={`https://doi.org/${displayDoi}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: 'var(--brand-colors-blue-600)',
                          textDecoration: 'underline',
                          userSelect: 'text',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          trackClick({
                            source: 'citeright.doi_link',
                            entityId: context?.reportId ?? undefined,
                            extra: {
                              page_context: pageContext,
                              suggestion_source: workData.source,
                            },
                          });
                        }}
                      >
                        {displayDoi}
                      </a>
                    </Body>
                    <button
                      onClick={handleCopyDoi}
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        color: isCopied ? '#4caf50' : '#666',
                        transition: 'color 0.2s',
                        pointerEvents: 'auto',
                      }}
                      onMouseEnter={(e) =>
                        !isCopied && (e.currentTarget.style.color = '#333')
                      }
                      onMouseLeave={(e) =>
                        !isCopied && (e.currentTarget.style.color = '#666')
                      }
                      title={isCopied ? 'Copied!' : 'Copy DOI to clipboard'}
                      type="button"
                    >
                      <Icon
                        name={isCopied ? 'check' : 'content_copy'}
                        size={16}
                      />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </RowComponent>
        </div>
      </ImpressionWatcher>
    );
  },
);

WorkCard.displayName = 'WorkCard';

export default WorkCard;

export const WorkCardActionButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  height: auto;
  padding: 4px 8px;
`;
